import React, { useState, useCallback, useMemo } from 'react';
import CreditNoteItemListBootstrap from './CreditNoteItemList';
import { getValidationState } from '../utils/invoiceValidation';

const CreditNoteFormBootstrap = ({
    formData,
    onFormDataChange,
    availableItems,
    loading = false,
    invoiceSettings,
    validationErrors = {},
    isEditMode = false,
    hasOriginalInvoice = false,
}) => {


    console.log('Credit note form data changed:', formData);


    // Track which fields have been touched for validation display
    const [touchedFields, setTouchedFields] = useState({});

    // Mark field as touched
    const handleFieldTouch = useCallback((fieldName) => {
        setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    }, []);

    // Handle items change
    const handleItemsChange = useCallback((items) => {
        console.log('Credit note items changed:', items);
        handleFieldTouch('items');
        onFormDataChange({ items });
    }, [onFormDataChange, handleFieldTouch]);

    // Handle rounding change
    const handleRoundingChange = useCallback((roundValue) => {
        onFormDataChange({ roundTotal: roundValue });
    }, [onFormDataChange]);

    // Handle date change with validation
    const handleDateChange = useCallback((e, dateType) => {
        handleFieldTouch(dateType);
        try {
            const date = new Date(e.target.value);
            if (!isNaN(date.getTime())) {
                onFormDataChange({ [dateType]: date });
            }
        } catch (error) {
            console.error('Invalid date:', error);
        }
    }, [onFormDataChange, handleFieldTouch]);

    // Handle text field changes
    const handleFieldChange = useCallback((fieldName, value) => {
        handleFieldTouch(fieldName);
        onFormDataChange({ [fieldName]: value });
    }, [onFormDataChange, handleFieldTouch]);

    // Format date for input
    const formatDate = useCallback((date) => {
        if (!date) return '';
        try {
            return date instanceof Date && !isNaN(date)
                ? date.toISOString().split('T')[0]
                : '';
        } catch (e) {
            return '';
        }
    }, []);

    // Get validation props for form fields
    const getFieldValidation = useCallback((fieldName) => {
        return getValidationState(fieldName, validationErrors, touchedFields);
    }, [validationErrors, touchedFields]);

    // Currency options for the dropdown
    const currencyOptions = useMemo(() => [
        { value: 'SEK', label: 'SEK - Svenska kronor' },
        { value: 'EUR', label: 'EUR - Euro' },
        { value: 'USD', label: 'USD - US Dollar' },
        { value: 'NOK', label: 'NOK - Norska kronor' },
        { value: 'DKK', label: 'DKK - Danska kronor' }
    ], []);

    return (
        <>
            <style>{`
            .credit-note-form-container {
                max-width: 100%;
                animation: fadeIn 0.3s ease-in;
            }
            
            @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
            }
            
            .credit-note-form-header {
                background: linear-gradient(135deg, #fef7f0 0%, #fed7aa 100%);
                border: 1px solid #fb923c;
                border-radius: 12px;
                padding: 1.5rem;
                margin-bottom: 2rem;
            }
            
            .credit-note-form-header-content {
                display: flex;
                align-items: center;
                gap: 1rem;
            }
            
            .credit-note-form-icon {
                width: 48px;
                height: 48px;
                background: #ea580c;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 1.25rem;
                flex-shrink: 0;
            }
            
            .credit-note-form-title {
                font-size: 1.25rem;
                font-weight: 700;
                color: #9a3412;
                margin: 0;
            }
            
            .credit-note-form-subtitle {
                font-size: 0.875rem;
                color: #c2410c;
                margin: 0.25rem 0 0 0;
                line-height: 1.4;
            }
            
            .original-invoice-info {
                background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                border: 1px solid #0284c7;
                border-radius: 12px;
                padding: 1.5rem;
                margin-bottom: 2rem;
                display: flex;
                align-items: center;
                justify-content: space-between;
            }
            
            .original-invoice-icon {
                width: 40px;
                height: 40px;
                background: #0284c7;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-size: 1.1rem;
                margin-right: 1rem;
                flex-shrink: 0;
            }
            
            .original-invoice-content {
                display: flex;
                align-items: center;
                flex-grow: 1;
            }
            
            .original-invoice-details h5 {
                margin: 0;
                font-size: 1rem;
                font-weight: 600;
                color: #0c4a6e;
            }
            
            .original-invoice-details small {
                color: #0369a1;
                font-size: 0.875rem;
            }
            
            .original-invoice-total {
                text-align: right;
                color: #0c4a6e;
                font-weight: 600;
            }
            
            .form-section {
                background: white;
                border-radius: 12px;
                padding: 2rem;
                margin-bottom: 1.5rem;
                border: 1px solid #e5e7eb;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .section-header {
                display: flex;
                align-items: center;
                gap: 0.75rem;
                margin-bottom: 1.5rem;
                padding-bottom: 1rem;
                border-bottom: 2px solid #f1f5f9;
            }
            
            .section-icon {
                width: 40px;
                height: 40px;
                background: #fef3c7;
                border: 1px solid #f59e0b;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #d97706;
                font-size: 1.25rem;
            }
            
            .section-title {
                font-size: 1.25rem;
                font-weight: 700;
                color: #1e293b;
                margin: 0;
            }
            
            .section-subtitle {
                font-size: 0.875rem;
                color: #64748b;
                margin: 0.25rem 0 0 0;
            }
            
            .form-label-modern {
                display: block;
                font-size: 0.875rem;
                font-weight: 600;
                color: #374151;
                margin-bottom: 0.5rem;
            }
            
            .required-asterisk {
                color: #dc2626;
                margin-left: 0.25rem;
            }
            
            .form-help {
                font-size: 0.75rem;
                color: #6b7280;
                margin-left: 0.5rem;
                font-weight: 400;
            }
            
            .form-control-modern {
                display: block;
                width: 100%;
                padding: 0.75rem 1rem;
                font-size: 0.875rem;
                font-weight: 500;
                line-height: 1.5;
                color: #1f2937;
                background-color: #ffffff;
                background-clip: padding-box;
                border: 1px solid #d1d5db;
                border-radius: 8px;
                transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
            }
            
            .form-control-modern:focus {
                color: #1f2937;
                background-color: #ffffff;
                border-color: #f59e0b;
                outline: 0;
                box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
            }
            
            .form-control-modern.is-invalid {
                border-color: #dc2626;
            }
            
            .form-control-modern.is-invalid:focus {
                border-color: #dc2626;
                box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
            }
            
            .form-select-modern {
                display: block;
                width: 100%;
                padding: 0.75rem 2.5rem 0.75rem 1rem;
                font-size: 0.875rem;
                font-weight: 500;
                line-height: 1.5;
                color: #1f2937;
                background-color: #ffffff;
                background-repeat: no-repeat;
                background-position: right 0.75rem center;
                background-size: 16px 12px;
                border: 1px solid #d1d5db;
                border-radius: 8px;
                transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                cursor: pointer;
            }
            
            .form-select-modern:focus {
                border-color: #f59e0b;
                outline: 0;
                box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
            }
            
            .form-select-modern.is-invalid {
                border-color: #dc2626;
            }
            
            .form-text-modern {
                margin-top: 0.5rem;
                font-size: 0.75rem;
                color: #6b7280;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            
            .invalid-feedback-modern {
                width: 100%;
                margin-top: 0.5rem;
                font-size: 0.75rem;
                color: #dc2626;
                font-weight: 500;
            }
            
            .form-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 1.5rem;
            }
            
            .form-grid-2 {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 1.5rem;
            }
            
            @media (max-width: 768px) {
                .credit-note-form-header {
                    padding: 1rem;
                }
                
                .credit-note-form-header-content {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.75rem;
                }
                
                .original-invoice-info {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 1rem;
                }
                
                .original-invoice-content {
                    width: 100%;
                }
                
                .original-invoice-total {
                    text-align: left;
                    width: 100%;
                }
                
                .form-section {
                    padding: 1.5rem;
                }
                
                .form-grid-2 {
                    grid-template-columns: 1fr;
                }
                
                .section-header {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.5rem;
                }
            }
            `}</style>

            <div className="credit-note-form-container">
                {/* Header Section */}
                <div className="credit-note-form-header">
                    <div className="credit-note-form-header-content">
                        <div className="credit-note-form-icon">
                            <i className="bi bi-receipt-cutoff"></i>
                        </div>
                        <div>
                            <h2 className="credit-note-form-title">Kreditfakturainformation</h2>
                            <p className="credit-note-form-subtitle">
                                Skapa en kreditfaktura för att kreditera delar av eller hela originalfakturan.
                                Ange anledning och välj vilka artiklar som ska krediteras.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Original Invoice Info */}
                {formData.originalInvoice && (
                    <div className="original-invoice-info">
                        <div className="original-invoice-content">
                            <div className="original-invoice-icon">
                                <i className="bi bi-file-earmark-text"></i>
                            </div>
                            <div className="original-invoice-details">
                                <h5>Originalfaktura: #{formData.originalInvoice.invoicenumber}</h5>
                                <small>
                                    Fakturadatum: {formData.originalInvoice.invoicedate ?
                                        new Date(formData.originalInvoice.invoicedate).toLocaleDateString('sv-SE') :
                                        'Ej angivet'
                                    }
                                </small>
                            </div>
                        </div>
                        <div className="original-invoice-total">
                            <strong>Totalt: </strong>
                            {new Intl.NumberFormat('sv-SE', {
                                style: 'currency',
                                currency: formData.originalInvoice.currency || 'SEK'
                            }).format(formData.originalInvoice.totals?.grand_total || formData.originalInvoice.total_with_tax || 0)}
                        </div>
                    </div>
                )}

                {/* Credit Note Details Section */}
                <div className="form-section">
                    <div className="section-header">
                        <div className="section-icon">
                            <i className="bi bi-calendar-event"></i>
                        </div>
                        <div>
                            <h2 className="section-title">Kreditfakturadetaljer</h2>
                            <p className="section-subtitle">Grundläggande information om kreditfakturan</p>
                        </div>
                    </div>

                    <div className="form-grid">
                        {/* Credit Note Number */}
                        <div>
                            <label className="form-label-modern">
                                Kreditfakturanummer
                                <span className="form-help">(Automatisk numrering)</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control-modern ${getFieldValidation('creditNoteNumber').isInvalid ? 'is-invalid' : ''}`}
                                value={formData.creditNoteNumber || ''}
                                onChange={(e) => handleFieldChange('creditNoteNumber', e.target.value)}
                                placeholder="Genereras automatiskt"
                                disabled={true}
                                readOnly={true}
                            />
                            {getFieldValidation('creditNoteNumber').isInvalid && (
                                <div className="invalid-feedback-modern">
                                    {validationErrors.creditNoteNumber}
                                </div>
                            )}
                        </div>

                        {/* Currency */}
                        <div>
                            <label className="form-label-modern">
                                Valuta
                                <span className="required-asterisk">*</span>
                            </label>
                            <select
                                className={`form-select-modern ${getFieldValidation('currency').isInvalid ? 'is-invalid' : ''}`}
                                value={formData.currency || 'SEK'}
                                onChange={(e) => handleFieldChange('currency', e.target.value)}
                            >
                                {currencyOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {getFieldValidation('currency').isInvalid && (
                                <div className="invalid-feedback-modern">
                                    {validationErrors.currency}
                                </div>
                            )}
                        </div>

                        {/* Credit Note Date */}
                        <div>
                            <label className="form-label-modern">
                                Kreditfakturadatum
                                <span className="required-asterisk">*</span>
                            </label>
                            <input
                                type="date"
                                className={`form-control-modern ${getFieldValidation('creditNoteDate').isInvalid ? 'is-invalid' : ''}`}
                                value={formatDate(formData.creditNoteDate)}
                                onChange={(e) => handleDateChange(e, 'creditNoteDate')}
                                required
                            />
                            {getFieldValidation('creditNoteDate').isInvalid && (
                                <div className="invalid-feedback-modern">
                                    {validationErrors.creditNoteDate}
                                </div>
                            )}
                        </div>

                        {/* Due Date */}
                        <div>
                            <label className="form-label-modern">
                                Förfallodatum
                                <span className="required-asterisk">*</span>
                            </label>
                            <input
                                type="date"
                                className={`form-control-modern ${getFieldValidation('dueDate').isInvalid ? 'is-invalid' : ''}`}
                                value={formatDate(formData.dueDate)}
                                onChange={(e) => handleDateChange(e, 'dueDate')}
                                required
                            />
                            {getFieldValidation('dueDate').isInvalid && (
                                <div className="invalid-feedback-modern">
                                    {validationErrors.dueDate}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Credit Note Items Section */}
                <div className="form-section">
                    <div className="section-header">
                        <div className="section-icon">
                            <i className="bi bi-list-ul"></i>
                        </div>
                        <div>
                            <h2 className="section-title">Artiklar att Kreditera</h2>
                            <p className="section-subtitle">
                                {formData.items?.length > 0
                                    ? `${formData.items.length} artikel${formData.items.length !== 1 ? 'ar' : ''} att kreditera`
                                    : 'Välj artiklar från originalfakturan som ska krediteras'
                                }
                            </p>
                        </div>
                    </div>

                    <CreditNoteItemListBootstrap
                        items={formData.items}
                        onItemsChange={handleItemsChange}
                        roundTotal={formData.roundTotal}
                        onRoundTotalChange={handleRoundingChange}
                        currency={formData.currency}
                        originalInvoiceNumber={formData.originalInvoice?.invoicenumber}
                    />
                </div>

                {/* Notes Section */}
                <div className="form-section">
                    <div className="section-header">
                        <div className="section-icon">
                            <i className="bi bi-chat-text"></i>
                        </div>
                        <div>
                            <h2 className="section-title">Anteckningar & Anledning</h2>
                            <p className="section-subtitle">Ange anledning till krediteringen och eventuella anteckningar</p>
                        </div>
                    </div>

                    <div>
                        <label className="form-label-modern">
                            Anledning till kreditering
                            <span className="form-help">(visas på kreditfakturan)</span>
                        </label>
                        <textarea
                            className={`form-control-modern ${getFieldValidation('notes').isInvalid ? 'is-invalid' : ''}`}
                            rows="4"
                            placeholder="Ange anledning till krediteringen, t.ex. felaktig fakturering, returnerade varor, rabatt..."
                            value={formData.notes || ''}
                            onChange={(e) => handleFieldChange('notes', e.target.value)}
                            maxLength={2000}
                        />
                        <div className="form-text-modern">
                            <span>Förklara varför kreditfakturan skapas - denna information hjälper vid bokföring och kundservice</span>
                            <span>{formData.notes?.length || 0} / 2000 tecken</span>
                        </div>
                        {getFieldValidation('notes').isInvalid && (
                            <div className="invalid-feedback-modern">
                                {validationErrors.notes}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreditNoteFormBootstrap;
